import { createAsyncThunk, createListenerMiddleware, createSlice, isAnyOf } from '@reduxjs/toolkit';

const initialState = {
  status: 'idle',
  error: null,
  // Moderation-queue sizes
  counts: {
    items: 0, lists: 0, authors: 0, sites: 0, tags: 0,
  },
  total: 0,
  // Distinguishes "not fetched yet" from "fetched and empty", so an empty queue
  // only acts (hide UI, redirect) once counts have actually loaded.
  countsLoaded: false,
  // Explicit, sticky session state toggled by the header review button, gated by
  // a non-empty queue.
  moderationActive: false,
};

export const fetchPendingCounts = createAsyncThunk(
  'pending/fetchCounts',
  async (_, { extra }) => {
    const { pendingApi } = extra;
    return pendingApi.getCounts();
  },
);

// Async thunks for approval workflow
export const approveItem = createAsyncThunk(
  'pending/approveItem',
  async ({ id }, { extra }) => {
    const { pendingApi } = extra;
    return pendingApi.approveItem(id);
  },
);

export const rejectItem = createAsyncThunk(
  'pending/rejectItem',
  async ({ id, reason = null }, { extra }) => {
    const { pendingApi } = extra;
    await pendingApi.rejectItem(id, reason);
    return { pendingItemId: id };
  },
);

export const batchModerateItems = createAsyncThunk(
  'pending/batchModerateItems',
  async ({ ids, verdict, reason = null }, { extra }) => {
    const { pendingApi } = extra;
    return pendingApi.batchModerateItems(ids, verdict, reason);
  },
);

export const approveAuthor = createAsyncThunk(
  'pending/approveAuthor',
  async ({ id }, { extra }) => {
    const { pendingApi } = extra;
    return pendingApi.approveAuthor(id);
  },
);

export const rejectAuthor = createAsyncThunk(
  'pending/rejectAuthor',
  async ({ id, reason = null }, { extra }) => {
    const { pendingApi } = extra;
    await pendingApi.rejectAuthor(id, reason);
    return { pendingAuthorId: id };
  },
);

export const batchModerateAuthors = createAsyncThunk(
  'pending/batchModerateAuthors',
  async ({ ids, verdict, reason = null }, { extra }) => {
    const { pendingApi } = extra;
    return pendingApi.batchModerateAuthors(ids, verdict, reason);
  },
);

export const approveList = createAsyncThunk(
  'pending/approveList',
  async ({ id }, { extra }) => {
    const { pendingApi } = extra;
    return pendingApi.approveList(id);
  },
);

export const rejectList = createAsyncThunk(
  'pending/rejectList',
  async ({ id, reason = null }, { extra }) => {
    const { pendingApi } = extra;
    await pendingApi.rejectList(id, reason);
    return { pendingListId: id };
  },
);

export const batchModerateLists = createAsyncThunk(
  'pending/batchModerateLists',
  async ({ ids, verdict, reason = null }, { extra }) => {
    const { pendingApi } = extra;
    return pendingApi.batchModerateLists(ids, verdict, reason);
  },
);

export const approveSite = createAsyncThunk(
  'pending/approveSite',
  async ({ id }, { extra }) => {
    const { pendingApi } = extra;
    return pendingApi.approveSite(id);
  },
);

export const rejectSite = createAsyncThunk(
  'pending/rejectSite',
  async ({ id, reason = null }, { extra }) => {
    const { pendingApi } = extra;
    await pendingApi.rejectSite(id, reason);
    return { pendingSiteId: id };
  },
);

export const batchModerateSites = createAsyncThunk(
  'pending/batchModerateSites',
  async ({ ids, verdict, reason = null }, { extra }) => {
    const { pendingApi } = extra;
    return pendingApi.batchModerateSites(ids, verdict, reason);
  },
);

const pendingSlice = createSlice({
  name: 'pending',
  initialState,
  reducers: {
    setModerationActive: (state, action) => {
      state.moderationActive = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPendingCounts.fulfilled, (state, action) => {
        state.counts = action.payload.counts;
        state.total = action.payload.total;
        state.countsLoaded = true;
      })
      .addCase(approveItem.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(approveItem.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(approveItem.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(rejectItem.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(rejectItem.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(rejectItem.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(batchModerateItems.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(batchModerateItems.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(batchModerateItems.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(approveAuthor.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(approveAuthor.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(approveAuthor.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(rejectAuthor.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(rejectAuthor.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(rejectAuthor.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(batchModerateAuthors.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(batchModerateAuthors.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(batchModerateAuthors.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(approveList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(approveList.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(approveList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(rejectList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(rejectList.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(rejectList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(batchModerateLists.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(batchModerateLists.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(batchModerateLists.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(approveSite.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(approveSite.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(approveSite.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(rejectSite.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(rejectSite.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(rejectSite.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(batchModerateSites.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(batchModerateSites.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(batchModerateSites.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  },
});

export const { setModerationActive } = pendingSlice.actions;

export const selectPendingCounts = (state) => state.pending.counts;
export const selectPendingTotal = (state) => state.pending.total;
export const selectPendingCountsLoaded = (state) => state.pending.countsLoaded;
export const selectModerationActive = (state) => state.pending.moderationActive;

// Any approve/reject changes the moderation queue, so refresh the counts that
// drive the header badge and drawer entity rows.
export const pendingListener = createListenerMiddleware();
pendingListener.startListening({
  matcher: isAnyOf(
    approveItem.fulfilled,
    rejectItem.fulfilled,
    batchModerateItems.fulfilled,
    approveAuthor.fulfilled,
    rejectAuthor.fulfilled,
    batchModerateAuthors.fulfilled,
    approveList.fulfilled,
    rejectList.fulfilled,
    batchModerateLists.fulfilled,
    approveSite.fulfilled,
    rejectSite.fulfilled,
    batchModerateSites.fulfilled,
  ),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(fetchPendingCounts());
  },
});

export default pendingSlice.reducer;
